import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Package, ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth, API } from '../App';

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('failed');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        withCredentials: true
      });

      setPaymentData(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Clear cart
        localStorage.removeItem('tagora_cart');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Continue polling
      setPollCount(attempts + 1);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('failed');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="card-brutal max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <div className="bg-accent w-20 h-20 rounded-full border-2 border-charcoal flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-charcoal animate-spin" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-bold text-charcoal font-heading mb-4">
              Vérification du paiement...
            </h1>
            <p className="text-charcoal/70">
              {pollCount > 0 && `Tentative ${pollCount}/5...`}
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="bg-secondary w-20 h-20 rounded-full border-2 border-charcoal flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-charcoal" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-bold text-charcoal font-heading mb-4">
              Paiement réussi!
            </h1>
            <p className="text-charcoal/70 mb-6">
              Merci pour votre commande. Vos goodies PingMe seront expédiés sous 48h.
            </p>
            {paymentData && (
              <div className="bg-surface-alt border-2 border-charcoal rounded-xl p-4 mb-6 text-left">
                <div className="flex items-center gap-3 mb-3">
                  <Package className="w-6 h-6 text-primary" />
                  <span className="font-bold text-charcoal">Détails de la commande</span>
                </div>
                <p className="text-sm text-charcoal/70">
                  Total: <span className="font-bold text-charcoal">
                    {(paymentData.amount_total / 100).toFixed(2)}€
                  </span>
                </p>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/dashboard"
                data-testid="go-to-dashboard"
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                Mon Dashboard
              </Link>
              <Link
                to="/shop"
                data-testid="continue-shopping"
                className="btn-outline flex-1 flex items-center justify-center gap-2"
              >
                Continuer mes achats
              </Link>
            </div>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="bg-danger w-20 h-20 rounded-full border-2 border-charcoal flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-white" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-bold text-charcoal font-heading mb-4">
              Paiement échoué
            </h1>
            <p className="text-charcoal/70 mb-6">
              Une erreur s'est produite lors du paiement. Votre carte n'a pas été débitée.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/shop"
                data-testid="retry-payment"
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                Réessayer
              </Link>
              <Link
                to="/"
                data-testid="go-home"
                className="btn-outline flex-1 flex items-center justify-center gap-2"
              >
                Accueil
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
